/**
 * main.js — Entry point for Wanderlight.
 * Initializes PixiJS, debug systems, EventBus, SceneManager, GameLoop.
 * Loads Boot scene, then transitions to MainMenu.
 */
import { initPixiApp } from './renderer/PixiSetup.js';
import eventBus from './core/EventBus.js';
import sceneManager from './core/SceneManager.js';
import gameLoop from './core/GameLoop.js';
import debugLog from './debug/DebugLog.js';
import debugConsole from './debug/DebugConsole.js';
import BootScene from './scenes/BootScene.js';
import MainMenuScene from './scenes/MainMenuScene.js';
import CharCreateScene from './scenes/CharCreateScene.js';
import WorldTravelScene from './scenes/WorldTravelScene.js';
import CombatScene from './scenes/CombatScene.js';
import DialogScene from './scenes/DialogScene.js';
import TownScene from './scenes/TownScene.js';
import dataStore from './systems/DataLoader.js';
import inputManager from './input/InputManager.js';
import focusManager from './input/FocusManager.js';
import questSystem from './systems/QuestSystem.js';
import companionSystem from './systems/CompanionSystem.js';
import combatAI from './systems/CombatAI.js';

async function main() {
  // Initialize window.__debug early
  window.__debug = {
    log: debugLog,
    console: debugConsole,
    commands: {},
    _onResize: null,
  };

  // Initialize debug systems
  debugLog.init();
  debugLog.info('Wanderlight starting...');

  // Check for test mode
  if (new URLSearchParams(window.location.search).has('testMode')) {
    window.__testMode = true;
    debugLog.info('Test mode enabled');
  }

  try {
    // Initialize PixiJS
    const app = await initPixiApp();
    debugLog.info(`PixiJS v8 initialized (${app.renderer.width}x${app.renderer.height})`);

    // Set up SceneManager with the PixiJS stage
    sceneManager.setStage(app.stage);

    // Wire resize through to SceneManager
    window.__debug._onResize = (w, h) => {
      sceneManager.resize(w, h);
    };

    // Also handle window resize
    window.addEventListener('resize', () => {
      const canvas = document.getElementById('game-canvas');
      if (canvas) {
        sceneManager.resize(canvas.clientWidth, canvas.clientHeight);
      }
    });

    // Initialize input system
    inputManager.init();
    inputManager.setFocusManager(focusManager);
    window.__inputManager = inputManager;
    window.__debug.sceneManager = sceneManager;
    debugLog.info('InputManager initialized with FocusManager');

    // Load all game data
    const dataResult = dataStore.loadAll();
    debugLog.info(`DataStore loaded: ${dataResult.success ? 'OK' : dataResult.errors.length + ' warnings'}`);

    // Register scenes
    sceneManager.register('Boot', BootScene);
    sceneManager.register('MainMenu', MainMenuScene);
    sceneManager.register('CharCreate', CharCreateScene);
    sceneManager.register('WorldTravel', WorldTravelScene);
    sceneManager.register('Combat', CombatScene);
    sceneManager.register('Dialog', DialogScene);
    sceneManager.register('Town', TownScene);

    // Initialize quest system
    questSystem.init();
    window.__debug.questSystem = questSystem;
    window.__debug.dataStore = dataStore;
    debugLog.info('QuestSystem initialized');

    // Initialize companion system
    window.__debug.companionSystem = companionSystem;
    debugLog.info('CompanionSystem initialized');

    // Initialize combat AI system
    window.__debug.combatAI = combatAI;
    debugLog.info('CombatAI initialized');

    // Initialize debug console (creates DOM, registers commands)
    debugConsole.init();

    // Start game loop
    gameLoop.start(app);
    debugLog.info('GameLoop started');

    // Start with Boot scene
    await sceneManager.transition('Boot');

    debugLog.info('Wanderlight boot complete');
  } catch (err) {
    debugLog.error(`Fatal initialization error: ${err.message}`);
    console.error('Wanderlight failed to start:', err);

    // Show fallback error message
    const gameCanvas = document.getElementById('game-canvas');
    if (gameCanvas) {
      gameCanvas.innerHTML = `
        <div style="color: #ff5252; padding: 40px; font-family: monospace; text-align: center;">
          <h2>Failed to initialize Wanderlight</h2>
          <p>${err.message}</p>
          <p>Check the browser console for details.</p>
        </div>
      `;
    }
  }
}

// Boot when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main);
} else {
  main();
}
